import { Component, OnInit, Input, OnDestroy, HostBinding } from '@angular/core';
import { Router } from '@angular/router';
import {
  Log,
  Path,
  AppFeatures,
  Tree,
  Permission,
  PermissionSubject,
  PermissionVerb,
  TreeNode,
  NodeType,
  AuthService
} from '@s8l/client-tree-lib';
import { Subscription, combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';

import { AlertService } from 'src/app/services/alert.service';
import { CmsServerService } from 'src/app/services/jsonrpc/cms-server.service';
import { CmsService } from 'src/app/services/cms.service';
import { NodeEditorService } from 'src/app/services/node-editor.service';
import { UserServerService } from 'src/app/services/jsonrpc/user-server.service';

import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit, OnDestroy {
  @HostBinding('class.collapsed') public isCollapsed = false;
  @Input() public mode = '';

  public tree: Tree;
  public root: any = {};
  public activePath: Path;

  public multiLanguage = environment.languages.length > 1;

  public modifiers = [];
  public children = {};
  public templates = {};

  @Input() public sidebarWidth = 240;

  private routeSubscription: Subscription = null;

  @HostBinding('style.width') public get sidebarWidthPx() {
    if (this.isCollapsed) {
      return '60px';
    }
    return (this.sidebarWidth + 40).toString() + 'px';
  }

  public get sidebarToggleWidthPx() {
    if (this.isCollapsed) {
      return '100px';
    }
    return (this.sidebarWidth + 90).toString() + 'px';
  }

  public get hasConfigurationControl(): boolean {
    return (
      this.cms.hasAccess(new Permission(PermissionSubject.Role, PermissionVerb.Read | PermissionVerb.Write)) &&
      this.cms.hasAccess(new Permission(PermissionSubject.Node, PermissionVerb.Read | PermissionVerb.Write))
    );
  }

  public get hasAccessControl(): boolean {
    return this.cms.hasAccess(new Permission(PermissionSubject.Role, PermissionVerb.Read | PermissionVerb.Write));
  }

  public get hasAchievement(): boolean {
    return (
      this.cms.hasFeatures([AppFeatures.Achievement]) &&
      this.cms.hasAccess(new Permission(PermissionSubject.Node, PermissionVerb.Read | PermissionVerb.Write))
    );
  }

  public get hasJourney(): boolean {
    return (
      this.cms.hasFeatures([AppFeatures.Journey]) &&
      this.cms.hasAccess(new Permission(PermissionSubject.Node, PermissionVerb.Read | PermissionVerb.Write))
    );
  }

  public get hasSurveys(): boolean {
    return this.cms.hasFeatures([AppFeatures.Survey]);
  }

  public get hasChat(): boolean {
    return this.cms.hasFeatures([AppFeatures.Chat]) && this.cms.hasAccess(new Permission(PermissionSubject.Role, PermissionVerb.Write));
  }

  public get hasReview(): boolean {
    return this.cms.hasFeatures([AppFeatures.QuizReview]);
  }

  public get hasVimeo() {
    return (
      this.cms.hasFeatures([AppFeatures.Vimeo]) &&
      this.cms.hasAccess(new Permission(new PermissionSubject('node', 'video'), PermissionVerb.Write))
    );
  }

  public get canCreate(): boolean {
    return this.cms.hasAccess(new Permission(PermissionSubject.Node, PermissionVerb.Write));
  }

  public get canReadDashboard(): boolean {
    return this.cms.hasAccess(new Permission(PermissionSubject.Stats, PermissionVerb.Read));
  }

  public get hasDashboardAccessControl(): boolean {
    return (
      this.cms.hasAccess(new Permission(PermissionSubject.Stats, PermissionVerb.Read | PermissionVerb.Write)) &&
      this.cms.hasAccess(new Permission(PermissionSubject.Role, PermissionVerb.Read | PermissionVerb.Write)) &&
      this.cms.hasAccess(new Permission(PermissionSubject.User, PermissionVerb.Read | PermissionVerb.Write))
    );
  }

  public get hasUserControl(): boolean {
    return this.cms.hasAccess(new Permission(PermissionSubject.User, PermissionVerb.Read));
  }

  public get hasRoleControl(): boolean {
    return this.cms.hasAccess(new Permission(PermissionSubject.Role, PermissionVerb.Read));
  }

  public get hasShopControl(): boolean {
    return (
      this.cms.hasFeatures([AppFeatures.Shopify]) &&
      this.cms.hasAccess(new Permission(PermissionSubject.Role, PermissionVerb.Read)) &&
      this.cms.hasAccess(
        new Permission(new PermissionSubject('shopify'), PermissionVerb.Read | PermissionVerb.Write | PermissionVerb.Delete)
      )
    );
  }

  public get hasVRSpeechControl(): boolean {
    return (
      this.cms.hasFeatures([AppFeatures.VRSpeech]) &&
      this.cms.hasAccess(new Permission(PermissionSubject.User, PermissionVerb.Read | PermissionVerb.Write))
    );
  }

  public get hasBookingControl(): boolean {
    return (
      this.cms.hasFeatures([AppFeatures.Booking]) &&
      this.cms.hasAccess(new Permission(new PermissionSubject('booking'), PermissionVerb.Read))
    );
  }

  public get hasReportsAccess(): boolean {
    return this.cms.hasAccess(new Permission(PermissionSubject.Stats, PermissionVerb.Read));
  }

  constructor(
    private router: Router,
    public cms: CmsService,
    private editor: NodeEditorService,
    private server: CmsServerService,
    private user: UserServerService,
    private auth: AuthService,
    private alert: AlertService
  ) {}

  public ngOnInit() {
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
    this.routeSubscription = combineLatest([this.editor.path, this.cms.tree])
      .pipe(filter(([, root]) => root != null))
      .subscribe(([path, tree]) => {
        Log.log('SidebarComponent', 'update', path, tree.root);
        this.tree = tree;
        this.root = tree.root;
        this.activePath = path;

        this.modifiers = this.cms.config.modifiers.map(m => {
          let path = null;
          if (m.condition.path) {
            path = new Path(m.condition.path).addFirst(this.root.name);
          }
          return {
            ...m,
            path
          };
        });

        for (const mod of this.modifiers) {
          this.children[mod.name] = this.getChildren([mod.name], mod.path);
          this.templates[mod.name] = this.getTemplates([mod.name]);
        }

        // eslint-disable-next-line @typescript-eslint/dot-notation
        this.children['none'] = this.getChildren([]);
        // eslint-disable-next-line @typescript-eslint/dot-notation
        this.templates['none'] = this.getTemplates([]);
      });
  }

  public ngOnDestroy() {
    this.routeSubscription.unsubscribe();
  }

  private getChildren(modifiers: string[], path?: Path) {
    const p = path || new Path([this.root.name]);
    const root = this.tree.get(p);
    if (!root || !root.children) {
      return [];
    }
    return root.children.filter(c => this.cms.config.matchesModifiers(c, modifiers));
  }

  private getTemplates(modifiers: string[]) {
    return this.cms.config.templates
      .map(page => {
        const p = Path.parse(page.path).addFirst(this.root.name);
        return {
          ...page,
          path: p.uri(),
          name: p.getLast(),
          type: NodeType.Node
        };
      })
      .filter(page => {
        const p = Path.parse(page.path);
        if (this.tree.get(p)) {
          // if node already extists, filter out
          return false;
        }
        return this.cms.config.matchesModifiers(page, modifiers);
      });
  }

  public hasFeature(feature: AppFeatures[]) {
    return this.cms.hasFeatures(feature);
  }

  public create() {
    return this.router.navigate(['/node', this.root.path, 'create']);
  }

  public createTemplate(template) {
    const path = Path.parse(template.path);
    return this.ensureNodes(path.removeLast(), template.meta)
      .then(() => {
        return this.server.treeCreate({
          path: template.path,
          meta: template.meta
        });
      })
      .then(() => this.router.navigate(['/' + this.mode, path.uri()]));
  }

  public canCreateModifer(mod: any): boolean {
    return mod.path.length > 1;
  }

  public createModifier(mod: any) {
    return this.ensureNodes(mod.path, mod.condition.meta).then(() => this.router.navigate(['/node', mod.path.uri(), 'create', mod.name]));
  }

  public openPermission() {
    return this.router.navigate(['/permission']);
  }

  public trackByUUID(index: number, node: TreeNode) {
    return node.uuid;
  }

  public trackByMod(index: number, mod: any) {
    return mod.name;
  }

  public switchLanguage(lang: string): Promise<any> {
    const currentUrl = this.router.url;
    const editNode = this.activePath.length > 1;
    const nodeUuid = editNode ? this.tree.get(this.activePath)?.uuid : null;
    return this.cms.applyLanguage(lang).then(tree => {
      if (editNode) {
        const path = nodeUuid ? tree.getByUUID(nodeUuid)?.path : null;
        if (!path || !path.length) {
          // currently selected node has no translation, navigate to start page
          return this.router.navigate(['/home']);
        } else {
          // navigate to translated node
          return this.router.navigate(['/node', path, this.mode]);
        }
      } else {
        // force reload of the current page in case it is dependent on the current language, but not on the current node (e.g. access control or journeys)
        return this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => this.router.navigate(['/' + currentUrl]));
      }
    });
  }

  private async ensureNodes(path: Path, meta) {
    for (const p of path.explode()) {
      if (this.tree.get(p)) {
        continue;
      }
      await this.server.treeCreate({
        path: p.uri(),
        meta
      });
    }
  }

  public logout() {
    return this.alert.fireConfirm('Log out', 'You are about to log out from the platform.').then(result => {
      if (result.value) {
        return this.user
          .accountLogout()
          .catch(() => {})
          .then(() => {
            this.auth.unauth();
            this.cms.deinit();
            return this.router.navigate(['/login']);
          });
      }
    });
  }

  public toggleCollapse() {
    this.isCollapsed = !this.isCollapsed;
  }
}
